import { cloneElement, useContext, useEffect, useRef, useState } from "react";
import { classnames } from "lib/helpers";
import _ from "lodash";
import DragCorner from "./DragCorner";
import { useStyle } from "lib/hooks";
import DragEdge from "./DragEdge";
import DropAnchor from "./DropAnchor";
import { toReactStyles } from "lib/react-styles";
import { rootNamespace } from "config";
import { TreeContext } from "pages/Editor";
import RefBroadcaster from "./RefBroadcaster";

/**
 * compute position of selection box
 *
 * @param {object} style
 * @returns {object}
 */
export function wrapperStyle(style) {
  const s = { top: 0, bottom: 0, right: 0, left: 0 };
  if (!style) return s;
  const apply_styles = {
    "margin-top": "top",
    "margin-right": "right",
    "margin-bottom": "bottom",
    "margin-left": "left",
  };
  Object.entries(style).forEach(([k, v]) => {
    if (k in apply_styles) s[apply_styles[k]] = v;
  });
  return s;
}

export default function DragContainer({
  component,
  children,
  path,
  editable = true,
  draggable = true,
}) {
  const ref = useRef();
  const [disabled, dragging] = useState(false);
  const [, onResize] = useStyle(["width", "height"], false);
  const { selection, context, publish, subscribe, ...props } =
    useContext(TreeContext);
  // is child a list
  // useMemo didnt work see issue #3
  const [fill, setFill] = useState();
  const onClick = (e) => {
    // TODO: prevent in render tab
    if (ref.current && editable) {
      const window = ref.current.ownerDocument.defaultView;
      const select = () => {
        e.stopPropagation();
        const { lastMouseRelease } = window;
        // TODO: find better way to prevent bubbling
        // cannot stop propagation from corner & edge handles
        if (lastMouseRelease && lastMouseRelease === e.timeStamp) return;
        publish(rootNamespace, "SELECT", path);
      };
      if (e.ctrlKey || e.metaKey) {
        if (window.skipClick) {
          window.skipClick = false;
          select();
        } else window.skipClick = true;
      } else {
        select();
      }
    }
  };
  const onDragStart = (e) => {
    if (selection && selection.path === path) {
      e.stopPropagation();
      const data = {
        item: component,
        action: "MOVE",
        from: path,
      };
      e.dataTransfer.setData("text/plain", JSON.stringify(data));
      e.dataTransfer.dropEffect = "move";
    }
  };
  // TODO: remove safety check
  // const clicked = selection.path === path;
  const clicked = _.get(selection, "path") === path;
  const rendered = () => {
    // check if component can be given a ref
    if (["object", "string"].includes(typeof children.type)) {
      const props = { ref };
      // fix for issue #4
      if (context !== "componenttree") {
        props.style = toReactStyles(component.style);
      }
      return cloneElement(children, props);
    }
    return children;
  };
  let style = {};
  // update parent when child changes
  // see issue #1
  useEffect(() => {
    const classList = _.get(ref, "current.classList");
    if (classList && classList.contains("ls")) return setFill(true);
    if (
      ["H1", "H2", "H3", "H4", "H5", "H6"].includes(
        _.get(ref, "current.tagName")
      )
    )
      return setFill(true);
    // TODO: not sure if required
    // setFill(false);
  }, [ref]);
  return (
    <TreeContext.Provider
      value={{
        ...props,
        context,
        selection,
        component,
        subscribe,
        publish,
        path,
      }}
    >
      <div className={classnames("ew", { a: clicked, d: disabled, f: fill })}>
        {editable && <RefBroadcaster {...{ ref, path }} />}
        {/* check if root to prevent from replacing root */}
        {draggable && <DropAnchor {...{ path }} />}
        <div className="ew-c" {...{ style, onDragStart, onClick, draggable }}>
          {rendered()}
          {clicked && editable && (
            <>
              <div className="ol" style={wrapperStyle(component.style)}>
                <DragEdge {...{ onResize, dragging, parent: ref }} vertical>
                  <div className="wx-eh v b"></div>
                </DragEdge>
                <DragEdge {...{ onResize, dragging, parent: ref }}>
                  <div className="wx-eh h r"></div>
                </DragEdge>
                <DragCorner {...{ onResize, dragging, parent: ref }}>
                  <div className="wx-ch br"></div>
                </DragCorner>
              </div>
            </>
          )}
        </div>
      </div>
    </TreeContext.Provider>
  );
}
